import React from 'react';
import {
  Box, Container, Typography, Stack, Chip, Avatar,
  useTheme, alpha, Fade, Slide
} from '@mui/material';
import {
  EmojiEvents, TrendingUp, Group, LocalFireDepartment,
  Star, WorkspacePremium
} from '@mui/icons-material';

const LeaderboardHero = ({
  topChampions = [],
  totalParticipants = 0,
  isAuthenticated = false,
  userRank = null,
  userStats = null
}) => {
  const theme = useTheme();

  // Get top 3 champions for podium display
  const podiumChampions = topChampions.slice(0, 3);

  return (
    <Box sx={{
      background: 'linear-gradient(135deg, #43a047 0%, #2e7d32 50%, #1b5e20 100%)',
      color: 'white',
      py: { xs: 4, sm: 6, md: 8 },
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.08) 0%, transparent 50%)',
        pointerEvents: 'none'
      }
    }}>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Main Title Section */}
        <Fade in={true} timeout={800}>
          <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '3rem', sm: '4.5rem', md: '6rem' },
                mb: { xs: 1, sm: 2 },
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                animation: 'bounce 2s ease-in-out infinite'
              }}
            >
              🏆
            </Typography>

            <Typography
              variant="h2"
              fontWeight={900}
              sx={{
                mb: { xs: 1, sm: 2 },
                background: 'linear-gradient(135deg, #ffffff 0%, #e8f5e8 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3.5rem' },
                lineHeight: { xs: 1.2, sm: 1.3 }
              }}
            >
              Eco Champions Leaderboard
            </Typography>

            <Typography
              variant="h5"
              sx={{
                opacity: 0.95,
                maxWidth: 600,
                mx: 'auto',
                mb: { xs: 3, sm: 4 },
                fontWeight: 400,
                textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                fontSize: { xs: '1rem', sm: '1.2rem', md: '1.5rem' },
                lineHeight: { xs: 1.4, sm: 1.5 },
                px: { xs: 2, sm: 0 }
              }}
            >
              Discover Bangladesh's environmental heroes who are making a difference through smart waste classification
            </Typography>

            {/* Statistics Row */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 4 }}
              justifyContent="center"
              alignItems="center"
              sx={{ mb: { xs: 3, sm: 4 } }}
            >
              <Slide in={true} direction="up" timeout={1000}>
                <Chip
                  icon={<Group sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} />}
                  label={`${totalParticipants > 0 ? totalParticipants : '1000+'} Eco Warriors`}
                  sx={{
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontSize: { xs: '0.8rem', sm: '1rem' },
                    fontWeight: 700,
                    height: { xs: 36, sm: 40 },
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    '& .MuiChip-icon': { color: 'white' }
                  }}
                />
              </Slide>

              <Slide in={true} direction="up" timeout={1200}>
                <Chip
                  icon={<TrendingUp sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} />}
                  label="Growing Community"
                  sx={{
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontSize: { xs: '0.8rem', sm: '1rem' },
                    fontWeight: 700,
                    height: { xs: 36, sm: 40 },
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    '& .MuiChip-icon': { color: 'white' }
                  }}
                />
              </Slide>

              {isAuthenticated && userRank && userRank > 0 && (
                <Slide in={true} direction="up" timeout={1400}>
                  <Chip
                    icon={<Star sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} />}
                    label={`Your Global Rank: #${userRank}`}
                    sx={{
                      background: 'rgba(255,215,0,0.9)',
                      color: '#1b5e20',
                      fontSize: { xs: '0.8rem', sm: '1rem' },
                      fontWeight: 700,
                      height: { xs: 36, sm: 40 },
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255,215,0,0.8)',
                      boxShadow: '0 0 20px rgba(255,215,0,0.4)',
                      '& .MuiChip-icon': { color: '#1b5e20' }
                    }}
                  />
                </Slide>
              )}
            </Stack>
          </Box>
        </Fade>

        {/* Podium Section top 3 */}

          {podiumChampions.length > 0 && (
              <Fade in timeout={800}>
                  <Box sx={{ mb: { xs: 2.5, sm: 4 } }}>
                      <Typography
                          variant="h4"
                          fontWeight={800}
                          textAlign="center"
                          sx={{
                              mb: { xs: 2, sm: 3 },
                              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                              fontSize: { xs: 'clamp(1.1rem, 4.5vw, 1.6rem)', sm: '2rem' }
                          }}
                      >
                          🥇 Current Top Champions
                      </Typography>

                      {/* Responsive grid: 2 columns on small phones, 3 on sm+ */}
                      <Box
                          sx={{
                              display: 'grid',
                              gap: { xs: 1.75, sm: 3 },
                              gridTemplateColumns: {
                                  xs: 'repeat( auto-fit, minmax(140px, 1fr) )',
                                  sm: 'repeat(3, minmax(160px, 1fr))'
                              },
                              justifyItems: 'center',
                              alignItems: 'end'
                          }}
                      >
                          {/* Map only the first 3 with medal styling */}
                          {podiumChampions.slice(0, 3).map((p, i) => {
                              const medal = ['🥇', '🥈', '🥉'][i];
                              const ring = ['#FFD700', '#C0C0C0', '#CD7F32'][i];
                              const shadow = [
                                  'rgba(255,215,0,0.6)',
                                  'rgba(192,192,192,0.4)',
                                  'rgba(205,127,50,0.4)'
                              ][i];

                              return (
                                  <Box key={p.userId || p.displayName} sx={{ textAlign: 'center', width: '100%' }}>
                                      {/* Avatar + medal */}
                                      <Box sx={{ position: 'relative', mb: 1 }}>
                                          {/* small crown only for #1 on sm+ */}
                                          {i === 0 && (
                                              <Box
                                                  sx={{
                                                      position: 'absolute',
                                                      top: { xs: -10, sm: -16 },
                                                      left: '50%',
                                                      transform: 'translateX(-50%)',
                                                      fontSize: { xs: '1.1rem', sm: '1.6rem' },
                                                      zIndex: 2
                                                  }}
                                              >
                                                  👑
                                              </Box>
                                          )}

                                          <Avatar
                                              src={p.profilePhotoUrl}
                                              sx={{
                                                  width: 'clamp(56px, 20vw, 96px)',
                                                  height: 'clamp(56px, 20vw, 96px)',
                                                  mx: 'auto',
                                                  border: `clamp(3px, 0.8vw, 6px) solid ${ring}`,
                                                  boxShadow: `0 10px 24px ${shadow}`
                                              }}
                                          >
                                              {p.displayName?.charAt(0)}
                                          </Avatar>

                                          {/* medal emoji in corner */}
                                          <Typography
                                              component="div"
                                              sx={{
                                                  position: 'absolute',
                                                  top: { xs: '5%', sm: '3%' },
                                                  right: { xs: '5%', sm: '3%' },
                                                  fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' },
                                                  zIndex: 3,
                                                  transform: 'translate(25%, -25%)',
                                                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                                                  transition: 'all 0.3s ease'
                                              }}
                                          >
                                              {medal}
                                          </Typography>
                                      </Box>

                                      {/* Name (single line, ellipsis) */}
                                      <Typography
                                          fontWeight={700}
                                          sx={{
                                              maxWidth: 180,
                                              mx: 'auto',
                                              fontSize: { xs: '0.9rem', sm: '1rem' },
                                              lineHeight: 1.2,
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis'
                                          }}
                                          title={p.displayName}
                                      >
                                          {p.displayName}
                                      </Typography>

                                      {/* Points chip (compact) */}
                                      <Chip
                                          label={`${p.totalPoints || 0} points`}
                                          size="small"
                                          sx={{
                                              mt: 0.75,
                                              px: 1.25,
                                              height: 26,
                                              fontSize: { xs: '0.72rem', sm: '0.8rem' },
                                              fontWeight: 700,
                                              color: i === 0 ? '#1b5e20' : 'white',
                                              background:
                                                  i === 0
                                                      ? 'linear-gradient(135deg, #FFD700 0%, #FFA000 100%)'
                                                      : i === 1
                                                          ? 'linear-gradient(135deg, #C0C0C0 0%, #9E9E9E 100%)'
                                                          : 'linear-gradient(135deg, #CD7F32 0%, #8D6E63 100%)',
                                              boxShadow: i === 0 ? '0 4px 16px rgba(255,215,0,0.35)' : 'none'
                                          }}
                                      />
                                  </Box>
                              );
                          })}
                      </Box>
                  </Box>
              </Fade>
          )}


          {/* User Quick Stats (if authenticated) */}
        {isAuthenticated && userStats && (
          <Fade in={true} timeout={2200}>
            <Box
              sx={{
                textAlign: 'center',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: { xs: 3, sm: 4 },
                p: { xs: 2, sm: 3 },
                border: '1px solid rgba(255,255,255,0.2)',
                mt: { xs: 3, sm: 4 }
              }}
            >
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  mb: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '1rem', sm: '1.25rem' }
                }}
              >
                Your Stats
              </Typography>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 2, sm: 3 }}
                justifyContent="center"
                divider={<Box sx={{ width: 1, height: 30, bgcolor: 'rgba(255,255,255,0.2)', display: { xs: 'none', sm: 'block' } }} />}
              >
                <Box>
                  <Typography
                    variant="h4"
                    fontWeight={800}
                    sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}
                  >
                    {userStats.totalPoints || 0}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.8,
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}
                  >
                    Total Points
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="h4"
                    fontWeight={800}
                    sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}
                  >
                    #{userRank || '?'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.8,
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}
                  >
                    Global Rank
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="h4"
                    fontWeight={800}
                    sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}
                  >
                    {userStats.totalClassifications || 0}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.8,
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}
                  >
                    Classifications
                  </Typography>
                </Box>
                {userStats.currentStreak > 0 && (
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}
                    >
                      🔥 {userStats.currentStreak}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        opacity: 0.8,
                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                      }}
                    >
                      Day Streak
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Fade>
        )}
      </Container>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          60% { transform: translateY(-5px); }
        }
        @keyframes glow {
          0% { filter: drop-shadow(0 0 10px rgba(255,215,0,0.8)); }
          100% { filter: drop-shadow(0 0 20px rgba(255,215,0,1)) drop-shadow(0 0 30px rgba(255,215,0,0.8)); }
        }
      `}</style>
    </Box>
  );
};

export default LeaderboardHero;
